import { memo, useCallback, useMemo } from 'react';
import { canAddScene } from '../../utils';
import { useAddScene } from './useAddScene';
import { getAbsolutePlayerTime, getPreviousSegmentAbsoluteTime } from '../../timeUtils';
import { useRemotionPlayerCurrentTime } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTime';
import Icon from '@/components/atoms/Icon';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import TooltipV2 from '@/components/TooltipV2';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';

function AddSceneButton({ compositeSegment }: { compositeSegment: CompositeSegment }) {
  const { currentTime } = useRemotionPlayerCurrentTime();
  const { clipData, fps } = useClipsContext();

  const { addScene } = useAddScene();

  const currentTimePosition =
    getAbsolutePlayerTime(currentTime, clipData) * fps -
    (compositeSegment ? getPreviousSegmentAbsoluteTime(compositeSegment, clipData) : 0) * fps;

  const handleAddScene = useCallback(() => {
    if (!compositeSegment) {
      return;
    }

    const trimOffset = compositeSegment.currentBounds.fromFrame;

    // Calculate the current frame position, offset by the composite segment's start frame
    const currentSegmentFrame =
      getAbsolutePlayerTime(currentTime, clipData) * fps -
      getPreviousSegmentAbsoluteTime(compositeSegment, clipData) * fps +
      trimOffset;

    // Add the new scene at the current frame position
    addScene(currentSegmentFrame, compositeSegment.id);
  }, [compositeSegment, currentTime, clipData, fps, addScene]);

  const isAddSceneDisabled = useMemo(() => {
    if (!compositeSegment) {
      return true;
    }
    return !canAddScene({
      compositeSegment,
      currentTime,
      clipData
    });
  }, [clipData, compositeSegment, currentTime]);

  if (isAddSceneDisabled || !compositeSegment) {
    return null;
  }

  return (
    <div
      className="group absolute top-1/2 z-50 -translate-y-1/2 opacity-0 transition-opacity duration-200 hover:opacity-100"
      style={{ left: `calc(${currentTimePosition}px * var(--timeline-zoom))` }}
    >
      <TooltipV2
        tooltipId="add-scene-tooltip"
        tooltipContent="Add scene"
        colorTheme="light"
        place="bottom"
        size="base"
        noArrow
        delayShow={400}
        tooltipElement={
          <button onClick={handleAddScene} disabled={isAddSceneDisabled}>
            <div className="z-[200] flex size-6 -translate-x-1/2 items-center justify-center rounded-full border border-slate-300 bg-slate-200">
              <Icon name="IconPlus" className="size-3 text-black" />
            </div>
          </button>
        }
        style={{
          transform: 'translateX(-12px)'
        }}
      />
    </div>
  );
}

export default memo(AddSceneButton);
